import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormattedDate from '../components/FormattedDate';
import { Mail, Phone, Calendar, Database, FileText, Briefcase, Plus, Edit, Trash, ChevronLeft, MapPin, Briefcase as BriefcaseIcon, RefreshCw } from 'lucide-react';
import api from '../utils/api';

const RecruiterDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [recruiter, setRecruiter] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [jobOffers, setJobOffers] = useState([]);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [suspendReason, setSuspendReason] = useState('');
    const [showPackModal, setShowPackModal] = useState(false);
    const [packs, setPacks] = useState([]);
    const [selectedPackId, setSelectedPackId] = useState('');

    const fetchRecruiter = useCallback(async () => {
        try {
            const response = await api.get(`/admin/recruiters/${id}`);
            setRecruiter(response.data.recruiter);
            setSubscriptions(response.data.subscriptions || []);
            setJobOffers(response.data.jobOffers);
            setStats(response.data.stats);
        } catch (error) {
            console.error('Error fetching recruiter:', error);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    const fetchPacks = async () => {
        try {
            const res = await api.get('/admin/packs');
            setPacks(res.data);
        } catch (error) {
            console.error("Error fetching packs", error);
        }
    }

    useEffect(() => {
        fetchRecruiter();
        fetchPacks();
    }, [fetchRecruiter]);

    const handleAssignPack = async () => {
        try {
            const response = await api.post('/admin/assign-pack', {
                recruiterId: id,
                packId: selectedPackId
            });
            setShowPackModal(false);
            if (response.data.request) {
                alert(response.data.message);
            } else {
                fetchRecruiter();
            }
        } catch (error) {
            console.error("Error assigning pack", error);
            alert("Erreur lors de l'attribution du pack");
        }
    };

    const handleRenewPack = async (packId) => {
        if (!window.confirm('Voulez-vous vraiment renouveler ce pack pour ce recruteur ?')) return;
        try {
            const response = await api.post('/admin/assign-pack', {
                recruiterId: id,
                packId: packId
            });
            if (response.data.request) {
                alert(response.data.message);
            } else {
                fetchRecruiter();
            }
        } catch (error) {
            console.error("Error renewing pack", error);
            alert("Erreur lors du renouvellement du pack");
        }
    };

    useEffect(() => {
        if (recruiter) {
            document.title = `${recruiter.company_name || 'Détail Recruteur'} - Admin Tekhdem`;
        }
    }, [recruiter]);

    const handleFeatureToggle = async (feature) => {
        try {
            await api.put(`/admin/recruiters/${id}/features`, {
                [feature]: !recruiter.features[feature]
            });
            fetchRecruiter();
        } catch (error) {
            console.error('Error toggling feature:', error);
        }
    };

    const handleSuspend = async () => {
        try {
            await api.put(`/admin/recruiters/${id}/suspend`, {
                suspended: !recruiter.suspended,
                reason: suspendReason
            });
            fetchRecruiter();
            setShowSuspendModal(false);
            setSuspendReason('');
        } catch (error) {
            console.error('Error suspending recruiter:', error);
        }
    };

    const handleDeleteOffer = async (offerId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) return;
        try {
            await api.delete(`/admin/job-offers/${offerId}`);
            fetchRecruiter();
        } catch (error) {
            console.error('Error deleting job offer:', error);
            alert('Erreur lors de la suppression de l\'offre');
        }
    };


    if (isLoading) {
        return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/recruiters')}
                        className="p-2 -ml-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{recruiter.company_name || 'Entreprise'}</h1>
                            {recruiter.suspended && (
                                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium border border-red-200">
                                    Compte Suspendu
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                            <div className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" />
                                {recruiter.user_id?.email}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" />
                                {recruiter.phone_number || 'Tel non renseigné'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowSuspendModal(true)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors border ${recruiter.suspended
                            ? 'bg-white border-slate-200 text-green-600 hover:bg-green-50 hover:border-green-200'
                            : 'bg-white border-slate-200 text-red-600 hover:bg-red-50 hover:border-red-200'
                            }`}
                    >
                        {recruiter.suspended ? 'Réactiver le compte' : 'Suspendre le compte'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Stats & Features */}
                <div className="space-y-6 lg:col-span-1">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-1">Offres</p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.totalJobOffers || 0}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-1">Missions</p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.totalMissions || 0}</p>
                        </div>
                    </div>


                    {/* Subscriptions Card */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-slate-900 dark:text-white">Abonnements Actifs</h3>
                            <button
                                onClick={() => { setSelectedPackId(''); setShowPackModal(true); }}
                                className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-xs font-medium transition-colors"
                            >
                                <Plus className="w-3 h-3 inline mr-1" />
                                Ajouter
                            </button>
                        </div>

                        <div className="space-y-3">
                            {subscriptions.length > 0 ? subscriptions.map(sub => {
                                const isExhausted = (sub.adCredits - sub.adCreditsUsed <= 0) || (sub.cvCredits !== -1 && sub.cvCredits - sub.cvCreditsUsed <= 0);
                                return (
                                    <div key={sub._id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h4 className="font-semibold text-sm text-slate-900 dark:text-white">{sub.pack?.name}</h4>
                                                <p className="text-xs text-slate-500">
                                                    Expire le <FormattedDate date={sub.endDate} />
                                                </p>
                                            </div>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {sub.status}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="text-slate-500 block">Annonces</span>
                                                <span className="font-medium text-slate-700 dark:text-slate-300">
                                                    {sub.adCredits - sub.adCreditsUsed} / {sub.adCredits}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-slate-500 block">CVthèque</span>
                                                <span className="font-medium text-slate-700 dark:text-slate-300">
                                                    {sub.pack?.cvCount === -1 ? 'Illimité' : `${sub.cvCredits - sub.cvCreditsUsed} / ${sub.cvCredits}`}
                                                </span>
                                            </div>
                                        </div>
                                        {isExhausted && sub.pack?._id && (
                                            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                                <button
                                                    onClick={() => handleRenewPack(sub.pack._id)}
                                                    className="w-full px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 border border-blue-200 dark:border-blue-800 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                                                >
                                                    <RefreshCw className="w-3.5 h-3.5" />
                                                    Renouveler ce pack
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )
                            }) : (
                                <p className="text-sm text-slate-500 text-center py-4">Aucun abonnement actif</p>
                            )}
                        </div>
                    </div>

                    {/* Features */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="font-semibold text-slate-900 dark:text-white">Fonctionnalités</h3>
                        </div>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <div className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${recruiter.features?.cvtheque ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
                                        <Database className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm text-slate-900 dark:text-white">CVthèque</p>
                                        <p className="text-xs text-slate-500">Accès aux profils</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={recruiter.features?.cvtheque || false}
                                        onChange={() => handleFeatureToggle('cvtheque')}
                                    />
                                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                            <div className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${recruiter.features?.missions ? 'bg-purple-50 text-purple-600' : 'bg-slate-50 text-slate-400'}`}>
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm text-slate-900 dark:text-white">Missions</p>
                                        <p className="text-xs text-slate-500">Publication de missions</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={recruiter.features?.missions || false}
                                        onChange={() => handleFeatureToggle('missions')}
                                    />
                                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Metadata Card */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 text-sm">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-500">Inscrit le {new Date(recruiter.user_id?.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Briefcase className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-500">{recruiter.industry || 'Secteur non défini'}</span>
                            </div>
                            {recruiter.website && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-4 h-4 flex items-center justify-center">🌐</div>
                                    <a href={recruiter.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                        {recruiter.website}
                                    </a>
                                </div>
                            )}
                            {recruiter.social_links?.linkedin && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-4 h-4 flex items-center justify-center">L</div>
                                    <a href={recruiter.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                        LinkedIn
                                    </a>
                                </div>
                            )}
                            {recruiter.social_links?.facebook && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-4 h-4 flex items-center justify-center">F</div>
                                    <a href={recruiter.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                        Facebook
                                    </a>
                                </div>
                            )}
                            {recruiter.social_links?.instagram && (
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="w-4 h-4 flex items-center justify-center">I</div>
                                    <a href={recruiter.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline truncate">
                                        Instagram
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Job Offers */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Offres d'emploi</h3>
                        <button
                            onClick={() => navigate(`/recruiters/${id}/new-offer`)}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Ajouter une offre
                        </button>
                    </div>

                    <div className="space-y-3">
                        {jobOffers.length > 0 ? jobOffers.map((job) => (
                            <div key={job._id} className="group bg-white dark:bg-slate-900 p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-sm hover:shadow-md">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h4 className="font-semibold text-slate-900 dark:text-white mb-1">{job.title?.title || job.title}</h4>
                                        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-400 capitalize">{job.contractType}</span>
                                            <span className={`px-2 py-0.5 rounded capitalize ${job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                {job.status === 'active' ? 'Active' : 'Archivée'}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {job.address?.city}, {job.address?.wilaya}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(job.createdAt).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => navigate(`/job-offers/${job._id}/edit`)}
                                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteOffer(job._id)}
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                                <div className="mx-auto w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                                    <Briefcase className="w-6 h-6 text-slate-400" />
                                </div>
                                <p className="text-slate-500 font-medium">Aucune offre publiée</p>
                                <p className="text-sm text-slate-400">Cliquez sur ajouter pour créer une offre</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Suspend Modal */}
            {showSuspendModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <div className="mb-4">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{recruiter.suspended ? 'Réactiver' : 'Suspendre'} l'accès</h3>
                            <p className="text-sm text-slate-500">
                                {recruiter.suspended
                                    ? 'Cette action rétablira l\'accès complet du recruteur à la plateforme.'
                                    : 'Le recruteur ne pourra plus se connecter ni gérer ses offres.'}
                            </p>
                        </div>

                        {!recruiter.suspended && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Motif de la suspension</label>
                                <textarea
                                    value={suspendReason}
                                    onChange={(e) => setSuspendReason(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Veuillez indiquer la raison..."
                                />
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => setShowSuspendModal(false)} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300">
                                Annuler
                            </button>
                            <button
                                onClick={handleSuspend}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium text-white shadow-sm ${recruiter.suspended
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-red-600 hover:bg-red-700'
                                    }`}
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Pack Modal */}
            {showPackModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        <div className="mb-4">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Ajouter un Pack</h3>
                            <p className="text-sm text-slate-500">
                                Sélectionnez un pack à ajouter au recruteur. Les crédits seront cumulés.
                            </p>
                        </div>

                        <div className="space-y-3 mb-6 overflow-y-auto flex-1 pr-2">
                            {packs.map((pack) => (
                                <div
                                    key={pack._id}
                                    onClick={() => setSelectedPackId(pack._id)}
                                    className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedPackId === pack._id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                                        : 'border-slate-200 dark:border-slate-800 hover:border-blue-300'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-semibold text-slate-900 dark:text-white">{pack.name}</span>
                                        <span className="text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{pack.price} DZD</span>
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Max: {pack.maxActiveOffers} offres | Durée: {pack.offerDurationDays} jours
                                    </div>
                                </div>
                            ))}


                        </div>

                        <div className="flex gap-3 pt-2 border-t border-slate-100 dark:border-slate-800 mt-auto">
                            <button onClick={() => setShowPackModal(false)} className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300">
                                Annuler
                            </button>
                            <button
                                onClick={handleAssignPack}
                                disabled={!selectedPackId}
                                className={`flex-1 px-4 py-2 rounded-lg font-medium text-white shadow-sm transition-colors ${!selectedPackId ? 'bg-slate-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                Ajouter le pack
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecruiterDetails;
